import React, { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { formatCurrencyValue } from '@eika/currency-input';
import { Loader } from '@eika/loader';

import { useFindEiendomsverdiMatrikkel } from '../../hooks/useEiendom';
import { useTranslation } from '../../translations';
import { BoligSalgFormData } from './boligSalg';

interface Props {
    soeknadId: string;
    eksisterendeLaanebeloep: number | null | undefined;
    isLoadingEngasjementer: boolean;
}

const BoligverdiInfoBox: FC<Props> = ({ soeknadId, eksisterendeLaanebeloep, isLoadingEngasjementer }) => {
    const { boligVerdi, boligSalg } = useTranslation();
    const { setValue, watch } = useFormContext<BoligSalgFormData>();
    const { eiendom, eTakst } = watch();
    const { data: eiendomsverdi, isLoading, isFetched } = useFindEiendomsverdiMatrikkel(soeknadId, eiendom);
    useEffect(() => {
        if (isFetched) {
            setValue('eiendom.eiendomsverdiWeb', eiendomsverdi || null);
        }
    }, [isFetched, eiendomsverdi, setValue]);

    if (isLoading || isLoadingEngasjementer) return <Loader className="padding-top-extra-large" />;

    return (
        <div className="info-box row-gap ">
            <div className="title">
                {eiendom ? `${eiendom.adresse}, ${eiendom.postnummer} ${eiendom.poststed}` : ''}
            </div>
            {eTakst?.verdi ? (
                <>
                    <div>
                        <span>{`${boligVerdi.infoNyEtakstverdi}: `}</span>
                        <span className="text-bold">{`${formatCurrencyValue(eTakst.verdi)} kr`}</span>
                    </div>
                    {eTakst?.fellesgjeld && (
                        <div>
                            <span>{`${boligVerdi.infoNyEtakstFellesgjeld}: `}</span>
                            <span className="text-bold">{`${formatCurrencyValue(eTakst.fellesgjeld)} kr`}</span>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div>
                        <span>{`${boligVerdi.infoAntattBoligverdi}: `}</span>
                        <span className="text-bold">
                            {eiendomsverdi?.verdi
                                ? `${formatCurrencyValue(eiendomsverdi?.verdi)} kr`
                                : `${boligVerdi.infoIkkeFunnet}`}
                        </span>
                    </div>
                    {eiendomsverdi?.fellesgjeld !== null && (
                        <div>
                            <span>{`${boligVerdi.infoAndelFellesgjeld}: `}</span>
                            <span className="text-bold">{`${formatCurrencyValue(eiendom?.eiendomsverdiWeb?.fellesgjeld || 0)} kr`}</span>
                        </div>
                    )}
                </>
            )}

            {eksisterendeLaanebeloep && (
                <div>
                    <span>{`${boligSalg.infoLaanIDag}: `}</span>
                    <span className="text-bold">{`${formatCurrencyValue(eksisterendeLaanebeloep)} kr`}</span>
                </div>
            )}
        </div>
    );
};

export default BoligverdiInfoBox;
